import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import { COMPANY_INFO, ROOF_FAQS, WINDOWS_FAQS, RENOVATION_FAQS } from "../data/companyData";
import { fetchCmsCollection, getOptimizedImageUrl } from "../lib/cms";
import ContactForm from "../components/ContactForm";
import FAQSection from "../components/FAQSection";
import LightboxModal from "../components/LightboxModal";
import QuickContactBanner from "../components/QuickContactBanner";

export default function ServiceDetailPage({ serviceType }) {
  const location = useLocation();
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  // Determine active service from prop or pathname
  const path = location.pathname.replace(/^\//, "");
  const type =
    serviceType ||
    (path.includes("tag")
      ? "tag"
      : path.includes("vindu") || path.includes("doer")
      ? "vinduer-doere"
      : path.includes("renovering")
      ? "renovering"
      : path.includes("tilbygning") || path.includes("vinterhave")
      ? "tilbygning-vinterhave"
      : path.includes("snedker")
      ? "snedkerarbejde"
      : "tag");

  // Fetch gallery images dynamically bound to this service's category tag
  useEffect(() => {
    async function loadServiceGallery() {
      const data = await fetchCmsCollection("gallery", {
        sort: "order",
        order: "asc",
        term: `gallery-category:${type}`
      });

      if (data.items && data.items.length > 0) {
        setGalleryImages(data.items);
      } else {
        // Fallback: load all gallery and filter
        const allData = await fetchCmsCollection("gallery", { sort: "order", order: "asc" });
        const filtered = (allData.items || []).filter(
          (item) => item.terms?.["gallery-category"]?.includes(type) || false
        );
        setGalleryImages(filtered.length > 0 ? filtered : allData.items || []);
      }
    }
    loadServiceGallery();
  }, [type]);

  const openLightbox = (index) => {
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  // Content configurations
  if (type === "tag") {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": ROOF_FAQS.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    };

    return (
      <>
        <SEO
          title="Nyt tag i Ribe, hvad passer til dit hus? | Søren Emil"
          description="Få nyt tag i Ribe med en løsning, der passer til husets konstruktion, stil og budget. Vi rådgiver om tagrenovering, materialer og asbesttag. Få et tilbud."
          ogTitle="Nyt tag i Ribe, Esbjerg mm. - Professionel tagrenovering her"
          ogDescription="Vi hjælper dig med alt inden for renovering af tag. Uanset om du skal have en komplet udskiftning af tag eller en ny tagkonstruktion. Kontakt os her."
          schema={faqSchema}
        />

        {/* Hero */}
        <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-house-chimney text-[#ce0611]" aria-hidden="true"></i>
                  <span>Tagarbejde & Tagrenovering</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
                  Nyt tag i Ribe, få en løsning der passer til huset
                </h1>
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Trænger boligen til nyt tag, hjælper vi dig med at finde en løsning, der passer til husets konstruktion, udtryk og dit budget. Tømrermester Søren Emil v/Jacob Heiselberg udfører tagarbejde for private og erhverv i Ribe, Esbjerg, Bramming og Skærbæk.
                </p>
                <div className="pt-2 flex flex-wrap gap-3">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
                    <span>Få et tilbud på nyt tag</span>
                  </Link>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center px-6 py-3.5 rounded-full bg-white hover:bg-stone-50 text-stone-800 font-semibold text-sm border border-stone-300 shadow-xs transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone mr-2 text-[#ce0611]" aria-hidden="true"></i>
                    <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 aspect-[4/3] bg-stone-200">
                  <img
                    src={getOptimizedImageUrl(
                      "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/skifertag-tagvinduer-linje-1-400x284.jpg",
                      { width: 800 }
                    )}
                    alt="Stillads og tagarbejde hos Tømrermester Søren Emil"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content sections */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 text-stone-700 leading-relaxed">
            {/* Section 1 */}
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Skal taget renoveres eller skiftes helt
              </h2>
              <p>
                Et slidt tag betyder ikke altid, at hele taget skal udskiftes. Først bør tagbelægning, undertag og den bærende konstruktion vurderes samlet. På den måde får du et fagligt grundlag for at vælge mellem en målrettet reparation, renovering af tag eller en komplet ny løsning.
              </p>
            </div>

            {/* Renovering vs udskiftning cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              <div className="p-6 rounded-xl bg-stone-50 border border-stone-200 space-y-3">
                <h3 className="text-lg font-bold text-stone-900 flex items-center">
                  <i className="fa-solid fa-wrench text-[#ce0611] mr-2 text-sm" aria-hidden="true"></i>
                  Hvornår giver renovering af tag mening
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Renovering af tag kan være relevant, når konstruktionen fortsat er sund, og problemerne er afgrænsede. Det kan blandt andet handle om slidte materialer, mindre skader eller behov for et nyt undertag. Løsningen afhænger altid af tagets faktiske tilstand.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-stone-50 border border-stone-200 space-y-3">
                <h3 className="text-lg font-bold text-stone-900 flex items-center">
                  <i className="fa-solid fa-house-circle-check text-[#ce0611] mr-2 text-sm" aria-hidden="true"></i>
                  Hvornår er udskiftning af tag den rigtige vej
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Udskiftning af tag er ofte den rigtige vej, når belægningen er udtjent, der er tilbagevendende utætheder, eller flere dele af opbygningen kræver arbejde. Her ser vi ikke kun på overfladen, men også på isolering, undertag og konstruktion, før løsningen fastlægges.
                </p>
                <ul className="text-xs text-stone-600 space-y-1.5 pt-2">
                  <li className="flex items-center"><i className="fa-solid fa-circle-exclamation text-[#ce0611] mr-2 text-[10px]" aria-hidden="true"></i> Revnede eller løse tagplader og tagsten</li>
                  <li className="flex items-center"><i className="fa-solid fa-circle-exclamation text-[#ce0611] mr-2 text-[10px]" aria-hidden="true"></i> Fugt, misfarvninger eller utætheder på loftet</li>
                  <li className="flex items-center"><i className="fa-solid fa-circle-exclamation text-[#ce0611] mr-2 text-[10px]" aria-hidden="true"></i> Slidt undertag eller tegn på en svækket konstruktion</li>
                  <li className="flex items-center"><i className="fa-solid fa-circle-exclamation text-[#ce0611] mr-2 text-[10px]" aria-hidden="true"></i> Et tag der kræver hyppige og gentagne reparationer</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvordan foregår montering af nyt tag
              </h2>
              <p>
                En god proces begynder med klare forventninger. Ved montering af nyt tag skal materialer, konstruktion og praktisk udførelse hænge sammen. Vi rådgiver om mulighederne og udarbejder et konkret tilbud ud fra opgaven, så du ved, hvad der indgår i den valgte løsning.
              </p>

              <h3 className="text-xl font-bold text-stone-900 pt-2">En grundig vurdering kommer først</h3>
              <p>
                Vi tager udgangspunkt i bygningen og det eksisterende tag. Tagflade, hældning, adgangsforhold og konstruktion har betydning for arbejdet. Samtidig afklarer vi, om undertag, isolering eller dele af tagkonstruktionen bør indgå, så projektet bliver planlagt som en samlet opgave.
              </p>
            </div>

            {/* Materials */}
            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Materialerne skal passe til huset
              </h2>
              <p>
                Valget mellem tegl, betontagsten, stål, eternit, skifer og tagpap handler både om udseende og funktion. Ikke alle materialer passer til enhver konstruktion. Derfor skal vægt, taghældning, vedligeholdelse, arkitektur og budget vurderes, inden den endelige løsning vælges.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm">
                  <strong className="text-stone-900 block mb-1">Tagbelægning til husets stil</strong>
                  Matchet til husets arkitektur og konstruktionens bæreevne.
                </div>
                <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm">
                  <strong className="text-stone-900 block mb-1">Nyt undertag</strong>
                  Korrekt diffusion og tæthed når den eksisterende løsning kræver udskiftning.
                </div>
                <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm">
                  <strong className="text-stone-900 block mb-1">Optimering af isolering</strong>
                  Mulighed for at vurdere og forbedre isoleringen i forbindelse med arbejdet.
                </div>
                <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm">
                  <strong className="text-stone-900 block mb-1">Præcis montering</strong>
                  Fokus på finish, holdbarhed og modstandsdygtighed mod det vestjyske vejr.
                </div>
              </div>
            </div>

            {/* Price & Asbestos */}
            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvad påvirker prisen på et nyt tag
              </h2>
              <p>
                Prisen på et nyt tag kan ikke vurderes ud fra tagets areal alene. Tagtype, hældning, kviste, gennembrydninger, adgangsforhold og materialevalg spiller ind. Det samme gør behovet for nyt undertag, ændringer i konstruktionen og arbejdet med at fjerne det gamle tag.
              </p>
              <p>
                Et tilbud på nyt tag bør derfor tage udgangspunkt i en konkret gennemgang og en tydelig afgrænsning af opgaven. Det giver et bedre overblik over både materialer og udførelse. Vi hjælper med at afstemme ønsker og budget uden at love en standardpris, der ikke passer til huset.
              </p>

              <h3 className="text-xl font-bold text-stone-900 pt-4">Hvad kræver et asbesttag før udskiftning</h3>
              <p>
                Et ældre eternittag kan indeholde asbest. Det bliver ofte kaldt et asbest tag, men materialet må ikke behandles som almindeligt byggeaffald, og nedtagning kræver særlig planlægning. Asbestholdige plader bør hverken knækkes, bores i eller håndteres uden de rette forholdsregler.
              </p>
              <p>
                Nedrivning af asbestholdigt materiale må som udgangspunkt kun udføres af en autoriseret virksomhed. Derfor skal ansvar, metode, emballering, transport og bortskaffelse afklares, før tagarbejdet starter. Det er vigtigt både for mennesker omkring byggeriet og for miljøet.
              </p>
            </div>
          </div>
        </section>

        {/* Dynamic Connected Gallery */}
        {galleryImages.length > 0 && (
          <section className="py-16 bg-stone-100/70 border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                  Udførte tagprojekter
                </h2>
                <p className="text-stone-600 text-sm mt-1">
                  Se eksempler på vores tagmontager, tegltage og tagrenoveringer i Ribe og omegn.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {galleryImages.slice(0, 8).map((imgItem, idx) => (
                  <div
                    key={imgItem.id || idx}
                    onClick={() => openLightbox(idx)}
                    className="group relative rounded-xl overflow-hidden bg-stone-200 border border-stone-300 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={getOptimizedImageUrl(imgItem.image?.[0], { width: 500 })}
                      alt={imgItem.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                      <span className="font-semibold text-xs sm:text-sm line-clamp-1">{imgItem.title}</span>
                      <span className="text-[10px] text-red-300 mt-0.5">Klik for at forstørre</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Service FAQ */}
        <FAQSection title="Spørgsmål om nyt tag & tagrenovering" faqs={ROOF_FAQS} />

        {/* CTA */}
        <QuickContactBanner
          title="Få et tilbud på nyt tag i Ribe og omegn"
          subtitle="Kontakt Tømrermester Søren Emil v/Jacob Heiselberg, hvis du vil have en faglig snak om dit tag. Fortæl gerne om bygningen, tagtypen og de problemer, du har lagt mærke til."
        />

        {/* Contact Form */}
        <section className="py-16 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactForm
              title="Indhent et tilbud på nyt tag"
              subtitle="Fortæl os om dit tagprojekt, så vender vi hurtigt tilbage."
            />
          </div>
        </section>

        <LightboxModal
          images={galleryImages}
          currentIndex={currentImgIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(idx) => setCurrentImgIndex(idx)}
        />
      </>
    );
  }

  // Windows and doors
  if (type === "vinduer-doere") {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": WINDOWS_FAQS.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    };

    return (
      <>
        <SEO
          title="Udskiftning af døre og vinduer i Ribe | Få et tilbud"
          description="Få professionel udskiftning af døre og vinduer i Ribe, Esbjerg, Bramming og Skærbæk. Få rådgivning, præcis montering og et konkret tilbud til din bolig."
          ogTitle="Nye vinduer & døre Ribe, Esbjerg mm - Udskiftning af vinduer"
          ogDescription="Vi er eksperter i montering af nye vinduer og døre. Uanset om du bygger nyt eller renoverer dit eksisterende hjem. Kontakt os her for et tilbud."
          schema={faqSchema}
        />

        {/* Hero */}
        <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-door-open text-[#ce0611]" aria-hidden="true"></i>
                  <span>Vinduer, Døre & Karme</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
                  Udskiftning af døre og vinduer i Ribe og omegn
                </h1>
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Overvejer du udskiftning af døre og vinduer, hjælper vi dig med at finde en løsning, der passer til boligens stil, dine behov og dit budget. Fra vores base ved Ribe udfører vi opgaver for private og erhverv i blandt andet Esbjerg, Bramming og Skærbæk.
                </p>
                <div className="pt-2 flex flex-wrap gap-3">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
                    <span>Få et tilbud på vinduer & døre</span>
                  </Link>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center px-6 py-3.5 rounded-full bg-white hover:bg-stone-50 text-stone-800 font-semibold text-sm border border-stone-300 shadow-xs transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone mr-2 text-[#ce0611]" aria-hidden="true"></i>
                    <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 aspect-[4/3] bg-stone-200">
                  <img
                    src={getOptimizedImageUrl(
                      "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/klassisk-vinterhave-med-havestue.jpg",
                      { width: 800 }
                    )}
                    alt="Udskiftning af vinduer og døre"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content sections */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 text-stone-700 leading-relaxed">
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvornår giver nye vinduer og døre mening?
              </h2>
              <p>
                Ældre elementer kan give træk, kuldenedfald og et højt varmetab, men alderen alene afgør ikke, om alt skal skiftes. Vi ser på rammer, karme, glas og funktion, så du får et fagligt grundlag for at vælge mellem reparation, nye ruder eller en komplet udskiftning.
              </p>
              <p>
                Nye vinduer og døre kan være relevante, når de eksisterende elementer er utætte, svære at åbne eller har tydelige tegn på råd og slid. Den rigtige løsning kan samtidig forbedre komforten, give mere dagslys og gøre boligen lettere at vedligeholde.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-stone-600 pt-2">
                <li className="flex items-center"><i className="fa-solid fa-check text-[#ce0611] mr-2 text-xs" aria-hidden="true"></i> Træk eller kulde tæt ved ruder og rammer</li>
                <li className="flex items-center"><i className="fa-solid fa-check text-[#ce0611] mr-2 text-xs" aria-hidden="true"></i> Kondens mellem glassene eller punkterede termoruder</li>
                <li className="flex items-center"><i className="fa-solid fa-check text-[#ce0611] mr-2 text-xs" aria-hidden="true"></i> Råd, revner eller andre skader i karme og rammer</li>
                <li className="flex items-center"><i className="fa-solid fa-check text-[#ce0611] mr-2 text-xs" aria-hidden="true"></i> Døre eller vinduer, der binder eller ikke lukker tæt</li>
              </ul>
            </div>

            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Vælg vinduer og døre, der klæder huset
              </h2>
              <p>
                Valget handler både om udseende, vedligeholdelse og boligens arkitektur. Vi arbejder med træ, kombinationen af træ og aluminium samt PVC og plast. Vi hjælper også med at vælge termoruder eller energiruder ud fra boligens forhold og den ønskede løsning.
              </p>
              <p>
                Udtrykket skal passe til huset. Det kan være dannebrogsvinduer, bondehusvinduer, sprosser, større panoramapartier eller Velux vinduer. Vi monterer også hoveddøre, terrassedøre, skydedøre samt indvendige og udvendige døre, så helheden bliver gennemtænkt.
              </p>
            </div>

            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Sådan får du korrekt montering af vinduer og døre
              </h2>
              <p>
                God montering af vinduer og døre handler om mere end at sætte elementerne på plads. Før arbejdet aftaler vi valg, mål og omfang. Under monteringen er præcis tilpasning, korrekt fastgørelse og en omhyggelig afslutning omkring karmene afgørende for funktion og finish.
              </p>
              <p>
                Når vinduer og døre er monteret, skal de kunne åbne, lukke og slutte tæt som planlagt. Vi har derfor fokus på justering og de synlige overgange omkring elementerne. Det giver et gennemført resultat, uanset om opgaven omfatter en enkelt dør eller flere elementer i hele huset.
              </p>
            </div>

            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Kan man montere nye vinduer i gamle huse?
              </h2>
              <p>
                Hvis dit hus trænger til en opdatering, kan en udskiftning af vinduer og døre være en effektiv måde at forbedre både komfort og energieffektivitet på. Vi har stor erfaring med at montere nye vinduer i 60’er huse og 70’er huse, hvor vi sikrer en løsning, der passer til husets arkitektur og stil.
              </p>
            </div>
          </div>
        </section>

        {/* Connected Gallery */}
        {galleryImages.length > 0 && (
          <section className="py-16 bg-stone-100/70 border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                  Udførte vindues- og dørprojekter
                </h2>
                <p className="text-stone-600 text-sm mt-1">
                  Se billeder af vores monteringer af vinduer, glaspartier og døre.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {galleryImages.slice(0, 8).map((imgItem, idx) => (
                  <div
                    key={imgItem.id || idx}
                    onClick={() => openLightbox(idx)}
                    className="group relative rounded-xl overflow-hidden bg-stone-200 border border-stone-300 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={getOptimizedImageUrl(imgItem.image?.[0], { width: 500 })}
                      alt={imgItem.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                      <span className="font-semibold text-xs sm:text-sm line-clamp-1">{imgItem.title}</span>
                      <span className="text-[10px] text-red-300 mt-0.5">Klik for at forstørre</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <FAQSection title="Spørgsmål om vinduer & døre" faqs={WINDOWS_FAQS} />

        <QuickContactBanner
          title="Klar til nye døre og vinduer i Ribe og omegn?"
          subtitle="Tømrermester Søren Emil v/Jacob Heiselberg hjælper med vinduer og døre i Ribe, Esbjerg, Bramming og Skærbæk. Fortæl os om dine ønsker, så udarbejder vi et uforpligtende tilbud."
        />

        <section className="py-16 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactForm
              title="Få et tilbud på vinduer & døre"
              subtitle="Udfyld formularen for rådgivning og opmåling."
            />
          </div>
        </section>

        <LightboxModal
          images={galleryImages}
          currentIndex={currentImgIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(idx) => setCurrentImgIndex(idx)}
        />
      </>
    );
  }

  // Renovering af hus
  if (type === "renovering") {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": RENOVATION_FAQS.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    };

    return (
      <>
        <SEO
          title="Renovering af hus i Ribe | Få den rette løsning til dit hjem"
          description="Få professionel renovering af hus i Ribe med rådgivning, præcist tømrerarbejde og koordinering fra start til slut. Kontakt os for et konkret tilbud."
          schema={faqSchema}
        />

        {/* Hero */}
        <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-hammer text-[#ce0611]" aria-hidden="true"></i>
                  <span>Renovering & Ombygning</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
                  Renovering af hus i Ribe og omegn
                </h1>
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Skal boligen have mere plads, bedre funktion eller et udtryk, der passer til hverdagen? Hos Tømrermester Søren Emil ved Jacob Heiselberg hjælper vi med renovering af hus i Ribe, Esbjerg, Bramming og Skærbæk. Du får rådgivning om løsningen og faglært tømrerarbejde med blik for materialer, præcision og finish.
                </p>
                <div className="pt-2 flex flex-wrap gap-3">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
                    <span>Få et tilbud på renovering</span>
                  </Link>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center px-6 py-3.5 rounded-full bg-white hover:bg-stone-50 text-stone-800 font-semibold text-sm border border-stone-300 shadow-xs transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone mr-2 text-[#ce0611]" aria-hidden="true"></i>
                    <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 aspect-[4/3] bg-stone-200">
                  <img
                    src={getOptimizedImageUrl(
                      "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/facade-renovering-stillads-tomrer-ribe-400x284.jpg",
                      { width: 800 }
                    )}
                    alt="Facaderenovering og tømrerarbejde"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content sections */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 text-stone-700 leading-relaxed">
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvordan får du en renovering, der fungerer i hverdagen?
              </h2>
              <p>
                En vellykket renovering begynder med at få behov, bygning og budget til at hænge sammen. Måske skal planløsningen forbedres, måske trænger enkelte rum til fornyelse, eller måske skal hele boligen gentænkes. Vi tager udgangspunkt i huset, så nye løsninger passer til det eksisterende byggeri.
              </p>
              <p>
                Vi rådgiver om materialer, udførelse og den praktiske rækkefølge, før arbejdet går i gang. Det skaber et bedre grundlag for beslutningerne og gør det lettere at afgrænse opgaven. Du får et konkret tilbud, som tager højde for boligens tilstand, dine valg og projektets omfang.
              </p>
            </div>

            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvad kan indgå i renovering af hus?
              </h2>
              <p>
                Vi løser både afgrænsede tømreropgaver og større byggeprojekter. Derfor kan vi hjælpe, når du vil opdatere et enkelt rum, ændre flere dele af boligen eller samle arbejdet i en større entreprise.
              </p>
              <ul className="space-y-2.5 text-sm text-stone-600 pt-2">
                <li className="flex items-start"><i className="fa-solid fa-check text-[#ce0611] mr-2.5 mt-1" aria-hidden="true"></i> Renovering af køkken med tømrerarbejde og snedkerarbejde tilpasset rummets brug og stil</li>
                <li className="flex items-start"><i className="fa-solid fa-check text-[#ce0611] mr-2.5 mt-1" aria-hidden="true"></i> Renovering af badeværelse med tømrerarbejde og koordinering med de relevante faggrupper</li>
                <li className="flex items-start"><i className="fa-solid fa-check text-[#ce0611] mr-2.5 mt-1" aria-hidden="true"></i> Udskiftning af vinduer og døre som en del af boligens samlede fornyelse</li>
                <li className="flex items-start"><i className="fa-solid fa-check text-[#ce0611] mr-2.5 mt-1" aria-hidden="true"></i> Nyt tag eller tagrenovering, når klimaskærmen også skal indgå i projektet</li>
                <li className="flex items-start"><i className="fa-solid fa-check text-[#ce0611] mr-2.5 mt-1" aria-hidden="true"></i> Total renovering med planlægning og koordinering af flere håndværksfag</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-stone-200">
              <div className="p-6 rounded-xl bg-stone-50 border border-stone-200 space-y-3">
                <h3 className="text-lg font-bold text-stone-900">Renovering af køkken</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Køkkenet skal både fungere i en travl hverdag og passe til resten af boligen. Ved renovering af køkken ser vi på tømrerarbejdet og snedkerarbejdet omkring løsningen samt på materialernes udtryk og holdbarhed.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-stone-50 border border-stone-200 space-y-3">
                <h3 className="text-lg font-bold text-stone-900">Renovering af badeværelse</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Et badeværelse stiller særlige krav til planlægning, materialer og samarbejde mellem håndværksfagene. Vi står for de relevante tømreropgaver og koordineringen i en samlet entreprise.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-stone-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Er total renovering den rigtige løsning for dit hus?
              </h2>
              <p>
                En total renovering kan være relevant, når flere rum og bygningsdele skal opdateres på samme tid. Det gælder ofte, hvis boligen skal have en ny indretning, køkken og bad skal fornyes, eller tag, vinduer og døre skal tænkes ind i samme projekt. Her er et samlet overblik særligt vigtigt.
              </p>
              <p>
                Vi kan følge projektet fra de første overvejelser til det færdige resultat og koordinere lokale håndværkere, så du får ét samlet ansvarsområde og overskuelig dialog.
              </p>
            </div>
          </div>
        </section>

        {/* Connected Gallery */}
        {galleryImages.length > 0 && (
          <section className="py-16 bg-stone-100/70 border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                  Udførte renoveringsopgaver
                </h2>
                <p className="text-stone-600 text-sm mt-1">
                  Se billeder fra vores facaderenoveringer og byggeprojekter.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {galleryImages.slice(0, 8).map((imgItem, idx) => (
                  <div
                    key={imgItem.id || idx}
                    onClick={() => openLightbox(idx)}
                    className="group relative rounded-xl overflow-hidden bg-stone-200 border border-stone-300 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={getOptimizedImageUrl(imgItem.image?.[0], { width: 500 })}
                      alt={imgItem.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                      <span className="font-semibold text-xs sm:text-sm line-clamp-1">{imgItem.title}</span>
                      <span className="text-[10px] text-red-300 mt-0.5">Klik for at forstørre</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <FAQSection title="Spørgsmål om renovering af hus" faqs={RENOVATION_FAQS} />

        <QuickContactBanner
          title="Få lokal hjælp til renovering i Ribe og omegn"
          subtitle="Overvejer du renovering af hus, køkken eller badeværelse, tager vi gerne en uforpligtende samtale om mulighederne og udarbejder et konkret tilbud."
        />

        <section className="py-16 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactForm
              title="Indhent tilbud på renovering"
              subtitle="Fortæl os om dine renoveringsplaner."
            />
          </div>
        </section>

        <LightboxModal
          images={galleryImages}
          currentIndex={currentImgIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(idx) => setCurrentImgIndex(idx)}
        />
      </>
    );
  }

  // Tilbygning & Vinterhave
  if (type === "tilbygning-vinterhave") {
    return (
      <>
        <SEO
          title="Tilbygning & Vinterhave | Tømrermester Jacob"
          description="Elegante udestuer, vinterhaver og tilbygninger med masser af dagslys og faglært tømrerarbejde i Ribe og omegn."
        />

        <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-tree-city text-[#ce0611]" aria-hidden="true"></i>
                  <span>Tilbygninger & Vinterhaver</span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
                  Tilbygninger og vinterhaver med lys og kvalitet
                </h1>
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Vi opfører elegante udestuer, vinterhaver og tilbygninger med masser af lysindfald og præcist snedker- og tømrerarbejde tilpasset din boligs arkitektur.
                </p>
                <div className="pt-2 flex flex-wrap gap-3">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <span>Få et tilbud på tilbygning</span>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 aspect-[4/3] bg-stone-200">
                  <img
                    src={getOptimizedImageUrl(
                      "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/vinterhave-tilbygning-med-lys-og-glas-400x284.jpg",
                      { width: 800 }
                    )}
                    alt="Vinterhave tilbygning med lys og glas"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        {galleryImages.length > 0 && (
          <section className="py-16 bg-white border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                  Udførte vinterhaver & tilbygninger
                </h2>
                <p className="text-stone-600 text-sm mt-1">
                  Se billeder af vores specialbyggede vinterhaver og havestuer.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {galleryImages.map((imgItem, idx) => (
                  <div
                    key={imgItem.id || idx}
                    onClick={() => openLightbox(idx)}
                    className="group relative rounded-xl overflow-hidden bg-stone-200 border border-stone-300 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={getOptimizedImageUrl(imgItem.image?.[0], { width: 500 })}
                      alt={imgItem.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                      <span className="font-semibold text-xs sm:text-sm line-clamp-1">{imgItem.title}</span>
                      <span className="text-[10px] text-red-300 mt-0.5">Klik for at forstørre</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <QuickContactBanner
          title="Drømmer du om en ny vinterhave eller tilbygning?"
          subtitle="Kontakt os for en uforpligtende samtale og besigtigelse."
        />

        <section className="py-16 bg-stone-50 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactForm
              title="Indhent tilbud på tilbygning"
              subtitle="Fortæl os om dine ønsker."
            />
          </div>
        </section>

        <LightboxModal
          images={galleryImages}
          currentIndex={currentImgIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(idx) => setCurrentImgIndex(idx)}
        />
      </>
    );
  }

  // Snedkerarbejde & Køkken
  return (
    <>
      <SEO
        title="Snedkerarbejde & Køkken | Tømrermester Jacob"
        description="Speciallavede snedkerkøkkener i massiv eg, inventar og detaljeret snedkerarbejde i Ribe og omegn."
      />

      <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-ruler-combined text-[#ce0611]" aria-hidden="true"></i>
                <span>Snedkerarbejde & Køkken</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
                Speciallavede snedkerkøkkener & unikt håndværk
              </h1>
              <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                Speciallavede snedkerkøkkener, indbyggede skabe, massive træbordplader og detaljeret snedkerarbejde skabt med præcision og kærlighed til faget.
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span>Få et tilbud på snedkerarbejde</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 aspect-[4/3] bg-stone-200">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/snedkerlavet-egetraeskoekken-med-teglgulv-400x284.jpg",
                    { width: 800 }
                  )}
                  alt="Snedkerlavet egetræskøkken"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {galleryImages.length > 0 && (
        <section className="py-16 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Snedkerprojekter & køkkener
              </h2>
              <p className="text-stone-600 text-sm mt-1">
                Se detaljer fra vores håndbyggede køkkener og træarbejder.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {galleryImages.map((imgItem, idx) => (
                <div
                  key={imgItem.id || idx}
                  onClick={() => openLightbox(idx)}
                  className="group relative rounded-xl overflow-hidden bg-stone-200 border border-stone-300 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                >
                  <img
                    src={getOptimizedImageUrl(imgItem.image?.[0], { width: 500 })}
                    alt={imgItem.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                    <span className="font-semibold text-xs sm:text-sm line-clamp-1">{imgItem.title}</span>
                    <span className="text-[10px] text-red-300 mt-0.5">Klik for at forstørre</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <QuickContactBanner
        title="Ønsker du et unikt snedkerkøkken eller specialinventar?"
        subtitle="Vi rådgiver om træsorter, overflader og skræddersyede løsninger."
      />

      <section className="py-16 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm
            title="Indhent tilbud på snedkerarbejde"
            subtitle="Send os en besked med dine ideer."
          />
        </div>
      </section>

      <LightboxModal
        images={galleryImages}
        currentIndex={currentImgIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setCurrentImgIndex(idx)}
      />
    </>
  );
}
